import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { HiLogout, HiOfficeBuilding, HiViewGrid, HiMenu, HiCreditCard, HiHome, HiUserGroup, HiClipboardCheck, HiShieldCheck, HiExclamation, HiDocumentText, HiCalendar, HiBadgeCheck, HiClock } from './Icons';
import { useAuth } from '../contexts/AuthContext';
import { getSocietyAdminData, setSocietyAdminData, getSocietySettings } from '../services/api';
import './SocietyAdminLayout.css';

interface SocietyAdminLayoutProps {
  children: React.ReactNode;
}

const SocietyAdminLayout: React.FC<SocietyAdminLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, displayName } = useAuth();
  const adminData = getSocietyAdminData();
  const [selectedSociety, setSelectedSociety] = useState(adminData?.societyId);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [reconciliationEnabled, setReconciliationEnabled] = useState(false);

  useEffect(() => {
    const sid = adminData?.societyId;
    if (!sid) return;
    getSocietySettings(sid)
      .then((res) => {
        if (res.success && res.data) {
          setReconciliationEnabled(res.data.reconciliation_enabled === 'yes');
        }
      })
      .catch(() => {});
  }, [adminData?.societyId]);

  const handleLogout = () => {
    logout();
    navigate('/society/login');
  };

  const handleSocietyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newId = parseInt(e.target.value, 10);
    if (adminData) {
      const society = adminData.societies.find((s) => s.society_id === newId);
      if (society) {
        setSocietyAdminData({ ...adminData, societyId: newId, societyName: society.society_name });
        setSelectedSociety(newId);
        window.location.reload();
      }
    }
  };

  const isActive = (path: string) => {
    if (path === '/society/ledger' && location.pathname.startsWith('/society/reconcile')) return true;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/society/overview') return { main: 'Overview', sub: 'Management Dashboard' };
    if (path === '/society/reconciliation') return { main: 'Reconciliation', sub: 'Statement Processing' };
    if (path.startsWith('/society/reconcile')) return { main: 'Reconcile', sub: 'Automated Matching' };
    if (path === '/society/ledger') return { main: 'Bank Ledger', sub: 'Statement Audit' };
    if (path === '/society/bill-payments') return { main: 'Bill Payments', sub: 'Pending approvals' };
    if (path === '/society/details') return { main: 'Society Details', sub: 'Society Management' };
    if (path === '/society/flats') return { main: 'Flats & Users', sub: 'Society Management' };
    if (path === '/society/pending-requests') {
      return { main: 'Pending Requests', sub: 'Society Management' };
    }
    if (path === '/society/flat-history') return { main: 'Flat History', sub: 'Society Management' };
    if (path === '/society/guards') return { main: 'Guards', sub: 'Society Management' };
    if (path === '/society/committee') return { main: 'Committee & Roles', sub: 'Society Management' };
    if (path === '/society/complaints') return { main: 'Complaints', sub: 'Complaint Management' };
    if (path === '/society/create-bill') return { main: 'Create Bill', sub: 'Generate bills for flats' };
    if (path === '/society/record-payment') return { main: 'Record Payment', sub: 'Counter / desk payments' };
    if (path === '/society/scheduled-bills') return { main: 'Scheduled Bills', sub: 'Recurring billing' };
    return { main: 'Society Admin Portal', sub: 'Dashboard' };
  };

  const pageTitle = getPageTitle();

  return (
    <div className={`sa-layout ${isCollapsed ? 'sa-sidebar-collapsed' : ''}`}>
      <aside className={`sa-sidebar ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="sa-sidebar-header">
          <div className="sa-society-badge">
            <HiOfficeBuilding size={20} />
          </div>
          <div className="sa-header-text">
            <h2>{displayName ? `Hi, ${displayName}` : 'Society Admin'}</h2>
            {adminData && adminData.societies.length > 1 ? (
              <select
                className="sa-society-select"
                value={selectedSociety}
                onChange={handleSocietyChange}
              >
                {adminData.societies.map((s) => (
                  <option key={s.society_id} value={s.society_id}>
                    {s.society_name}
                  </option>
                ))}
              </select>
            ) : (
              <span className="sa-society-name">{adminData?.societyName || 'Society'}</span>
            )}
          </div>
        </div>
        <nav className="sa-nav">
          <Link
            to="/society/overview"
            className={`sa-nav-link ${isActive('/society/overview') ? 'active' : ''}`}
          >
            <HiViewGrid size={20} /> <span>Overview</span>
          </Link>

          <div className="sa-nav-section">
            <div className="sa-nav-section-title">Society Management</div>
            <Link
              to="/society/details"
              className={`sa-nav-link ${isActive('/society/details') ? 'active' : ''}`}
            >
              <HiHome size={20} /> <span>Society Details</span>
            </Link>
            <Link
              to="/society/flats"
              className={`sa-nav-link ${isActive('/society/flats') ? 'active' : ''}`}
            >
              <HiUserGroup size={20} /> <span>Flats & Users</span>
            </Link>
            <Link
              to="/society/pending-requests"
              className={`sa-nav-link ${isActive('/society/pending-requests') ? 'active' : ''}`}
            >
              <HiClock size={20} /> <span>Pending Requests</span>
            </Link>
            <Link
              to="/society/flat-history"
              className={`sa-nav-link ${isActive('/society/flat-history') ? 'active' : ''}`}
            >
              <HiClipboardCheck size={20} /> <span>Flat History</span>
            </Link>
            <Link
              to="/society/committee"
              className={`sa-nav-link ${isActive('/society/committee') ? 'active' : ''}`}
            >
              <HiBadgeCheck size={20} /> <span>Committee & Roles</span>
            </Link>
            <Link
              to="/society/guards"
              className={`sa-nav-link ${isActive('/society/guards') ? 'active' : ''}`}
            >
              <HiShieldCheck size={20} /> <span>Guards</span>
            </Link>
            <Link
              to="/society/complaints"
              className={`sa-nav-link ${isActive('/society/complaints') ? 'active' : ''}`}
            >
              <HiExclamation size={20} /> <span>Complaints</span>
            </Link>
          </div>

          <div className="sa-nav-section">
            <div className="sa-nav-section-title">Billing</div>
            <Link
              to="/society/create-bill"
              className={`sa-nav-link ${isActive('/society/create-bill') ? 'active' : ''}`}
            >
              <HiDocumentText size={20} /> <span>Create Bill</span>
            </Link>
            <Link
              to="/society/record-payment"
              className={`sa-nav-link ${isActive('/society/record-payment') ? 'active' : ''}`}
            >
              <HiCreditCard size={20} /> <span>Record Payment</span>
            </Link>
            <Link
              to="/society/scheduled-bills"
              className={`sa-nav-link ${isActive('/society/scheduled-bills') ? 'active' : ''}`}
            >
              <HiCalendar size={20} /> <span>Scheduled Bills</span>
            </Link>
          </div>

          {reconciliationEnabled && (
            <div className="sa-nav-section">
              <div className="sa-nav-section-title">Maintain Accounts</div>
              <Link
                to="/society/ledger"
                className={`sa-nav-link ${isActive('/society/ledger') ? 'active' : ''}`}
              >
                <div className="sa-nav-step">1</div> <span>Bank Ledger</span>
              </Link>
              <Link
                to="/society/reconciliation"
                className={`sa-nav-link ${isActive('/society/reconciliation') || isActive('/society/reconcile') ? 'active' : ''}`}
              >
                <div className="sa-nav-step">2</div> <span>Reconciliation</span>
              </Link>
              <Link
                to="/society/bill-payments"
                className={`sa-nav-link ${isActive('/society/bill-payments') ? 'active' : ''}`}
              >
                <HiCreditCard size={20} /> <span>Bill payments</span>
              </Link>
            </div>
          )}
        </nav>
        <div className="sa-sidebar-footer">
          <button onClick={handleLogout} className="sa-logout-button">
            <HiLogout size={20} /> <span>Logout</span>
          </button>
        </div>
      </aside>
      <main className={`sa-main ${isCollapsed ? 'collapsed' : ''}`}>
        <header className="sa-header">
          <button
            className="sa-header-toggle"
            onClick={() => setIsCollapsed(!isCollapsed)}
            title={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
          >
            <HiMenu size={22} />
          </button>
          <div className="sa-header-title-group">
            <h1>{pageTitle.main}</h1>
            <span className="sa-header-subtitle">{pageTitle.sub}</span>
          </div>
          <div className="sa-header-society-name">{adminData?.societyName}</div>
        </header>
        <div className="sa-content">{children}</div>
      </main>
    </div>
  );
};

export default SocietyAdminLayout;
